/**
 * Simple read-only lead/subscriber tables for the Admin dashboard, extracted
 * verbatim from Admin.jsx. Each is presentational — it takes its rows + an
 * export handler.
 */
import React from "react";
import { formatDate, downloadCsv } from "./adminHelpers";

const ExportHeader = ({ title, onExport, testid }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="ek-heading text-xl">{title}</h2>
    <button type="button" onClick={onExport} className="ek-btn ek-btn-outline text-[12px]" data-testid={testid}>
      Export CSV
    </button>
  </div>
);

export const ContactsPanel = ({ contacts }) => (
  <div data-testid="admin-contacts-panel">
    <ExportHeader title="Contact submissions" testid="export-contacts-csv" onExport={() => downloadCsv("/admin/contacts.csv", "ekselens-contacts.csv")} />
    <div className="overflow-x-auto border ek-line">
      <table className="min-w-full text-[13px]">
        <thead className="ek-surface-2 ek-text-muted text-left">
          <tr>
            <th className="px-3 py-2">Date</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Organisation</th><th className="px-3 py-2">Interest</th><th className="px-3 py-2">Message</th>
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 ? (
            <tr><td colSpan={6} className="px-3 py-10 text-center ek-text-muted text-[13px]"><div className="font-medium ek-text-soft">No discovery enquiries yet.</div><div className="mt-1 ek-text-muted">Submissions from the <code className="text-[12px]">/contact</code> form land here. A notification email is also sent to <code className="text-[12px]">NOTIFICATION_EMAIL</code>.</div></td></tr>
          ) : contacts.map((c) => (
            <tr key={c.id} className="border-t ek-line" data-testid={`contact-row-${c.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{formatDate(c.created_at)}</td>
              <td className="px-3 py-2 font-medium">{c.full_name}</td>
              <td className="px-3 py-2">{c.email}</td>
              <td className="px-3 py-2">{c.organisation}</td>
              <td className="px-3 py-2">{c.area_of_interest}</td>
              <td className="px-3 py-2 max-w-md truncate">{c.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const BrochuresPanel = ({ brochures }) => (
  <div data-testid="admin-brochures-panel">
    <ExportHeader title="Brochure download requests" testid="export-brochures-csv" onExport={() => downloadCsv("/admin/brochure-requests.csv", "ekselens-brochure-requests.csv")} />
    <div className="overflow-x-auto border ek-line">
      <table className="min-w-full text-[13px]">
        <thead className="ek-surface-2 ek-text-muted text-left">
          <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Organisation</th><th className="px-3 py-2">Brochure</th></tr>
        </thead>
        <tbody>
          {brochures.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-10 text-center ek-text-muted text-[13px]"><div className="font-medium ek-text-soft">No brochure downloads yet.</div><div className="mt-1 ek-text-muted">Each priority-offer card on the home page has a brochure modal — requests are logged here with the visitor&rsquo;s contact details and the offer they downloaded.</div></td></tr>
          ) : brochures.map((b) => (
            <tr key={b.id} className="border-t ek-line">
              <td className="px-3 py-2 whitespace-nowrap">{formatDate(b.created_at)}</td>
              <td className="px-3 py-2 font-medium">{b.full_name}</td>
              <td className="px-3 py-2">{b.email}</td>
              <td className="px-3 py-2">{b.organisation}</td>
              <td className="px-3 py-2">{b.offer_title}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const ResourceLeadsPanel = ({ resourceLeads }) => (
  <div data-testid="admin-resource-leads-panel">
    <ExportHeader title="Resource download leads" testid="export-resource-leads-csv" onExport={() => downloadCsv("/admin/resource-leads.csv", "ekselens-resource-leads.csv")} />
    <div className="overflow-x-auto border ek-line">
      <table className="min-w-full text-[13px]">
        <thead className="ek-surface-2 ek-text-muted text-left">
          <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Organisation</th><th className="px-3 py-2">Resource</th></tr>
        </thead>
        <tbody>
          {resourceLeads.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-10 text-center ek-text-muted text-[13px]"><div className="font-medium ek-text-soft">No resource downloads yet.</div><div className="mt-1 ek-text-muted">Visitors who request a downloadable resource from the <code className="text-[12px]">/resources</code> page appear here.</div></td></tr>
          ) : resourceLeads.map((r) => (
            <tr key={r.id} className="border-t ek-line">
              <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
              <td className="px-3 py-2 font-medium">{r.full_name}</td>
              <td className="px-3 py-2">{r.email}</td>
              <td className="px-3 py-2">{r.organisation}</td>
              <td className="px-3 py-2">{r.resource_title}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const NewsletterPanel = ({ subscribers }) => (
  <div data-testid="admin-newsletter-panel">
    <ExportHeader title="Newsletter subscribers" testid="export-newsletter-csv" onExport={() => downloadCsv("/admin/newsletter.csv", "ekselens-newsletter.csv")} />
    <div className="overflow-x-auto border ek-line">
      <table className="min-w-full text-[13px]">
        <thead className="ek-surface-2 ek-text-muted text-left">
          <tr><th className="px-3 py-2">Signed up</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Confirmed at</th></tr>
        </thead>
        <tbody>
          {subscribers.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-10 text-center ek-text-muted text-[13px]"><div className="font-medium ek-text-soft">No newsletter subscribers yet.</div><div className="mt-1 ek-text-muted">Footer / insights newsletter sign-ups appear here. Export the list when you&rsquo;re ready to send the first digest.</div></td></tr>
          ) : subscribers.map((s) => (
            <tr key={s.id} className="border-t ek-line" data-testid={`subscriber-row-${s.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{formatDate(s.created_at)}</td>
              <td className="px-3 py-2 font-medium">{s.email}</td>
              <td className="px-3 py-2">
                <span className={`inline-block px-2 py-0.5 text-[11px] font-medium tracking-wide uppercase ${
                  s.status === "confirmed" ? "bg-green-100 text-green-800" : s.status === "unsubscribed" ? "bg-zinc-200 ek-text-soft" : "bg-amber-100 text-amber-800"
                }`}>{s.status}</span>
              </td>
              <td className="px-3 py-2">{s.source}</td>
              <td className="px-3 py-2 whitespace-nowrap">{s.confirmed_at ? formatDate(s.confirmed_at) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
